import React, { createContext, useContext, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

export type Crumb = { label: string; to?: string }

type BreadcrumbState = { crumbs: Crumb[]; pathname: string | null; loading: boolean }

type BreadcrumbContextValue = {
  state: BreadcrumbState
  setCrumbs: (crumbs: Crumb[], pathname: string, loading: boolean) => void
}

const EMPTY: { crumbs: Crumb[]; loading: boolean } = { crumbs: [], loading: false }

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  state: { crumbs: [], pathname: null, loading: false },
  setCrumbs: () => undefined,
})

export const BreadcrumbProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<BreadcrumbState>({ crumbs: [], pathname: null, loading: false })
  const setCrumbs = useMemo(
    () => (crumbs: Crumb[], pathname: string, loading: boolean) => setState({ crumbs, pathname, loading }),
    [],
  )
  const value = useMemo(() => ({ state, setCrumbs }), [state, setCrumbs])
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>
}

export const useBreadcrumbs = (): { crumbs: Crumb[]; loading: boolean } => {
  const { state } = useContext(BreadcrumbContext)
  const { pathname } = useLocation()
  return state.pathname === pathname ? { crumbs: state.crumbs, loading: state.loading } : EMPTY
}

export const useSetBreadcrumbs = () => useContext(BreadcrumbContext).setCrumbs
